import { useParams, useNavigate } from 'react-router-dom';
import { articleDetails, hotRecommendations, categories } from '@/mocks/videoData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = slug ? articleDetails[slug] : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex flex-col items-center justify-center bg-background-50">
          <p className="text-foreground-600 text-lg mb-4">未找到该内容</p>
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg cursor-pointer transition-colors whitespace-nowrap"
          >
            返回首页
          </a>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbs = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${getCategorySlug(article.category)}` },
    { label: article.title, href: `/article/${slug}` },
  ];

  function getCategorySlug(cat: string): string {
    const found = categories.find((c) => c.name === cat);
    return found ? found.slug : 'movie';
  }

  const relatedVideos = hotRecommendations.slice(0, 4);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="article"
        pageTitle={`${article.title} - 蘑菇视频`}
        pageDescription={article.description}
        pageUrl={`https://mogu.video/article/${slug}`}
        articleData={{
          title: article.title,
          description: article.description,
          author: '蘑菇视频',
          imageUrl: article.imageUrl,
        }}
        breadcrumbs={breadcrumbs}
      />
      <Navbar />

      <main className="min-h-screen bg-background-50">
        <article>
          <div className="pt-20 pb-4 px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                {breadcrumbs.map((crumb, i) => (
                  <span key={crumb.href} className="flex items-center gap-2">
                    {i > 0 && (
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line text-xs"></i>
                      </span>
                    )}
                    {i === breadcrumbs.length - 1 ? (
                      <span className="text-foreground-800 font-medium">{crumb.label}</span>
                    ) : (
                      <a
                        href={crumb.href}
                        onClick={(e) => { e.preventDefault(); navigate(crumb.href); }}
                        className="hover:text-primary-500 cursor-pointer transition-colors whitespace-nowrap"
                      >
                        {crumb.label}
                      </a>
                    )}
                  </span>
                ))}
              </nav>

              <div className="flex flex-col lg:flex-row gap-8 mb-10">
                <div className="w-full lg:w-80 flex-shrink-0">
                  <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                    {article.title}
                  </h1>

                  <div className="flex items-center gap-3 flex-wrap mb-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-xs"></i>
                      </span>
                      {article.rating}
                    </span>
                    <span className="text-sm text-foreground-600">{article.year}</span>
                    <span className="text-sm text-foreground-600">{article.category}</span>
                    <span className="text-sm text-foreground-600">{article.duration}</span>
                  </div>

                  <p className="text-base text-foreground-700 leading-relaxed mb-6">
                    {article.description}
                  </p>

                  <div className="flex flex-col gap-3 p-5 bg-background-100 rounded-lg">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-foreground-500 w-14 flex-shrink-0">导演</span>
                      <span className="text-sm text-foreground-800">{article.director}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-foreground-500 w-14 flex-shrink-0">主演</span>
                      <span className="text-sm text-foreground-800">{article.cast}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-foreground-500 w-14 flex-shrink-0">类型</span>
                      <span className="text-sm text-foreground-800">{article.genre}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-foreground-500 w-14 flex-shrink-0">时长</span>
                      <span className="text-sm text-foreground-800">{article.duration}</span>
                    </div>
                  </div>

                  <button className="mt-6 inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg cursor-pointer transition-colors whitespace-nowrap">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-play-fill text-lg"></i>
                    </span>
                    立即观看
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 pb-10">
            <div className="max-w-5xl mx-auto">
              <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: article.content }} />
            </div>
          </div>
        </article>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedVideos.map((item) => (
                <a
                  key={item.id}
                  href={`/article/${item.slug}`}
                  onClick={(e) => { e.preventDefault(); navigate(`/article/${item.slug}`); }}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3 bg-background-200">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="inline-block px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded">
                        {item.rating}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{item.category} · {item.year}</p>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}